import { FC, useState } from 'react'

import { Radio } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { yupResolver } from '@hookform/resolvers/yup'

import { device } from 'src/Styled/variables'
import TikTokIconLight from 'src/Assets/Svg/tiktok_light.svg?react'
import { useThemeMode } from 'src/Hooks/useThemeMode'

import {
  SurveyFormValues,
  surveyFormSchema,
  shuffleSurveyGroups,
} from './helpers'
import { SurveyButtons } from './SurveyButtons'
import { useSendFeedBack } from './useSendFeedBack'
import { SurveyItem } from './SurveyItem'

const Form = styled.form`
  height: 100%;
  display: flex;
  max-width: 600px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  @media ${device.tabletMin} {
    margin: 0 auto;
    max-width: 600px;
    min-width: 600px;
    gap: 16px;
  }
`

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const GroupBlock = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  border-radius: 16px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.color.brandGreen : theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  padding: 16px;
`

const GroupTitle = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`

const GroupItems = styled.div`
  display: flex;
  flex-direction: column;
`

export const SurveyForm: FC = () => {
  const { loading, onSendFeedback } = useSendFeedBack()
  const { resolvedTheme } = useThemeMode()
  const [surveyGroups] = useState(shuffleSurveyGroups)

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
  } = useForm<SurveyFormValues>({
    resolver: yupResolver(surveyFormSchema),
    mode: 'onChange',
  })

  const handleSave = async (data: SurveyFormValues): Promise<void> => {
    await onSendFeedback(data)
  }

  return (
    <Form onSubmit={handleSubmit(handleSave)} translate='no'>
      <Controller
        control={control}
        name='resource'
        render={({ field: { onChange, value } }) => (
          <StyledRadioGroup
            onChange={(e) => {
              onChange(e)
              setValue('description', '')
            }}
            value={value}
          >
            {surveyGroups.map((group) => (
              <GroupBlock
                key={group.title}
                $isActive={group.items.some((item) => item.title === value)}
              >
                <GroupTitle>{group.title}</GroupTitle>

                <GroupItems>
                  {group.items.map(
                    ({ title, Icon, placeholder, inputType, maxLength }) => {
                      const ResolvedIcon =
                        title === 'TikTok' && resolvedTheme === 'dark'
                          ? TikTokIconLight
                          : Icon

                      return (
                        <Controller
                          key={title}
                          control={control}
                          name='description'
                          render={({ field }) => (
                            <SurveyItem
                              Icon={ResolvedIcon}
                              inputType={inputType}
                              placeholder={placeholder}
                              title={title}
                              isChecked={value === title}
                              maxLength={maxLength}
                              {...field}
                            />
                          )}
                        />
                      )
                    },
                  )}
                </GroupItems>
              </GroupBlock>
            ))}
          </StyledRadioGroup>
        )}
      />

      <SurveyButtons disabled={!isValid} loading={loading} />
    </Form>
  )
}
